import {
  AddIcon,
  ArrowHeadRightIcon,
  Grid,
  GridColumn,
  GridRow,
  LazyLoader,
  Modal,
  Select,
  type SelectOptionType,
  showToast,
  useDefaultSelecting,
} from '@bp/ui-components';
import type { RowSelectionState } from '@tanstack/react-table';
import { graphql } from 'client/gql';
import type React from 'react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';
import { useQuery } from 'urql';
import { OrganizationConfigContext } from '../../context/OrganizationConfigContext';
import { useCreateSelectOptions } from '../../hooks/useCreateSelectOptions';
import { backendApi } from '../../utils/backendApi';
import { createForeignRefId, SyncMaster } from '../../utils/create-foreignRefId';
import { showSuccessToast } from '../../utils/showSuccessToast';
import { BpCard } from '../BpCard/BpCard';
import { SchoolyearForm } from '../SchoolyearForm/SchoolyearForm';
import { type ProcuratGroup, useProcuratGroups } from './hooks/useProcuratGroups';
import { ImportTutorsModal, PersonAssignments } from './modals/ImportTutorsModal';
import { ProcuratClassesTable, type ProcuratClassesTableData } from './tables/ProcuratClassesTable';

const ProcuratClassesQueryDocument = graphql(`
  query ProcuratClassesData($organizationUuid: ID!, $schoolYearUuid: ID!) {
    schoolYears(where: { organization: { uuid: $organizationUuid } }) {
      __typename
      uuid
      name
    }
    classes(where: { organization: { uuid: $organizationUuid }, schoolYear: { uuid: $schoolYearUuid } }) {
      __typename
      uuid
      name
      foreignRefId
      updatedAt
    }
  }
`);

type ImportData = {
  id: number;
  schoolYearUuid: string | null;
  importParents: boolean;
  bpClassUuids: string[];
};

export const ProcuratClasses: React.FC = () => {
  const { t } = useTranslation();
  const { organization, currentSchoolYear } = useContext(OrganizationConfigContext);

  const { groups, isFetching: isGroupsFetching } = useProcuratGroups();

  // State management
  const { rowSelection, onRowSelectionChange } = useDefaultSelecting();
  const [createSchoolYearModalOpen, setCreateSchoolYearModalOpen] = useState(false);
  const [importWithParents, setImportWithParents] = useState<RowSelectionState>({});
  const [importing, setImporting] = useState(false);
  const [selectedSchoolyearId, setSelectedSchoolyearId] = useState<number>(0);
  const [selectedBpSchoolyearUuid, setSelectedBpSchoolyearUuid] = useState<string | null>(currentSchoolYear.uuid);
  const [porcuratGroupBpClasses, setPorcuratGroupBpClasses] = useState<Record<number, string[]>>({});
  const [isTutorsModalOpen, setIsTutorsModalOpen] = useState(false);
  const [classIds, setClassIds] = useState<number[]>([]);

  // Queries
  const [{ data }, refetch] = useQuery({
    query: ProcuratClassesQueryDocument,
    variables: {
      organizationUuid: organization?.uuid ?? '',
      schoolYearUuid: selectedBpSchoolyearUuid ?? '',
    },
    pause: !selectedBpSchoolyearUuid,
  });

  // Memoized values
  const existingBpSchoolyears = useMemo(() => data?.schoolYears ?? [], [data?.schoolYears]);

  const procuratSchoolyearsSelectData = useMemo(() => {
    const schoolYears = groups?.schoolYears ?? [];

    return schoolYears
      .map((sy) => ({
        id: sy.id,
        label: `${sy.name} - ${groups?.facilities.find((f) => f.id === sy.parentGroupId)?.name ?? ''}`,
      }))
      .filter((sy) => {
        // show only schoolYears with classes
        const classesInYear = groups?.classes.filter((pclass: ProcuratGroup) => pclass.parentGroupId === sy?.id);
        return classesInYear && classesInYear.length > 0;
      })
      .sort((a, b) => b.label.localeCompare(a.label));
  }, [groups]);

  const procuratSchoolyearSelectOpts = useCreateSelectOptions(procuratSchoolyearsSelectData, 'id', 'label');
  const bpSchoolyearSelectOpts = useCreateSelectOptions(existingBpSchoolyears, 'uuid', 'name');
  const bpClassesSelectOpts = useCreateSelectOptions(data?.classes, 'uuid', 'name');

  const procuratClassesData = useMemo<ProcuratClassesTableData[]>(() => {
    if (!groups?.classes) return [];

    return groups.classes
      .map((pclass) => {
        const bpClasses = data?.classes.filter((c) =>
          c.foreignRefId?.includes(createForeignRefId(SyncMaster.procurat, pclass.id.toString())),
        );

        return {
          ...pclass,
          lastImport:
            bpClasses?.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0]
              ?.updatedAt ?? '',
          bpClasses: porcuratGroupBpClasses[pclass.id]
            ? porcuratGroupBpClasses[pclass.id].map((c) => {
                return {
                  value: c,
                  label: data?.classes.find((bpClass) => bpClass.uuid === c)?.name ?? c,
                };
              })
            : (bpClasses?.map((c) => {
                return {
                  value: c.uuid,
                  label: c.name,
                };
              }) ?? []),
        };
      })
      .filter((pclass) => pclass.parentGroupId === selectedSchoolyearId)
      .sort((a, b) => (a.sortKey ?? 0) - (b.sortKey ?? 0));
  }, [selectedSchoolyearId, groups, data?.classes, porcuratGroupBpClasses]);

  // Handlers
  const handleSchoolyearSelectChange = useCallback(
    (e: SingleValue<SelectOptionType>) => {
      onRowSelectionChange({});
      setSelectedSchoolyearId((e?.value as number) ?? 0);
    },
    [onRowSelectionChange],
  );

  const handleProfilesAssign = useCallback(() => {
    setIsTutorsModalOpen(true);
    const selectedClassesForImport = procuratClassesData.filter((_, index) =>
      Object.keys(rowSelection).includes(index.toString()),
    );
    setClassIds(selectedClassesForImport.map((group) => group.id));
  }, [rowSelection, procuratClassesData]);

  const handleImport = async (personAssignments: PersonAssignments | null) => {
    setImporting(true);
    setIsTutorsModalOpen(false);

    showToast(t('procuratImport.importStarted'), {
      type: 'info',
    });

    const selectedClassesForImport = procuratClassesData.filter((_, index) =>
      Object.keys(rowSelection).includes(index.toString()),
    );

    const classItems: ImportData[] = selectedClassesForImport.map((group) => ({
      id: group.id,
      schoolYearUuid: selectedBpSchoolyearUuid,
      importParents: importWithParents[group.id] ?? false,
      bpClassUuids: porcuratGroupBpClasses[group.id] ?? [],
    }));

    try {
      const resp = await backendApi.put('/procurat/classes/import', JSON.stringify({ classItems, personAssignments }));

      if (resp.ok) {
        const data = (await resp.json()) as {
          importedProfilesCount: number;
          updatedProfilesCount: number;
          importedClassesCount: number;
        };

        showSuccessToast(
          t('procuratImport.importStatus', {
            importedProfilesCount: data.importedProfilesCount,
            updatedProfilesCount: data.updatedProfilesCount,
            importedClassesCount: data.importedClassesCount,
          }),
        );

        refetch({ requestPolicy: 'network-only' });
        onRowSelectionChange({});
      } else {
        showToast(resp.statusText, { type: 'error' });
      }
    } catch {
      showToast(t('import.importError'), { type: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const handleCreateSchoolYearClose = () => {
    refetch();
    setCreateSchoolYearModalOpen(false);
  };

  const handleSetBpClasses = useCallback((bpClassUuids: string[], procuratId: number) => {
    setPorcuratGroupBpClasses((prev) => {
      return {
        ...prev,
        [procuratId]: bpClassUuids,
      };
    });
  }, []);

  return (
    <>
      {importing ? (
        <LazyLoader forceHeight='30vh' />
      ) : (
        <>
          <BpCard
            header={{
              headline: t('schoolyears.title'),
              actions: [
                {
                  callback: () => setCreateSchoolYearModalOpen(true),
                  text: t('common.addType', {
                    type: t('schoolyears.titleSingular'),
                  }),
                  icon: <AddIcon />,
                },
              ],
            }}
          >
            {isGroupsFetching ? (
              <LazyLoader embedded />
            ) : (
              <Grid useFormGap>
                <GridRow>
                  <GridColumn width={6}>
                    <Select
                      label={t('procuratImport.schoolYearsInProcurat')}
                      isSearchable={true}
                      options={procuratSchoolyearSelectOpts}
                      placeholder={t('procuratImport.selectSchoolYear')}
                      value={procuratSchoolyearSelectOpts.find((o) => o.value === selectedSchoolyearId)}
                      onChange={(e) => handleSchoolyearSelectChange(e as SingleValue<SelectOptionType>)}
                      name='schoolYear'
                      menuPosition='fixed'
                    />
                  </GridColumn>
                  <GridColumn width={1} align='center' className='mt-4'>
                    <ArrowHeadRightIcon />
                  </GridColumn>
                  <GridColumn width={5}>
                    <Select
                      label={t('procuratImport.importToSchoolYear')}
                      isSearchable={true}
                      options={bpSchoolyearSelectOpts}
                      placeholder={t('procuratImport.selectSchoolYear')}
                      value={bpSchoolyearSelectOpts.find((o) => o.value === selectedBpSchoolyearUuid)}
                      onChange={(e) =>
                        setSelectedBpSchoolyearUuid((e as SingleValue<SelectOptionType> | null)?.value as string)
                      }
                      name='targetSchoolYear'
                      menuPosition='fixed'
                    />
                  </GridColumn>
                </GridRow>
              </Grid>
            )}
          </BpCard>

          <BpCard className='mt-3' header={{ headline: t('classes.titlePlural') }} noPadding>
            {isGroupsFetching ? (
              <LazyLoader embedded forceHeight='15vh' />
            ) : (
              <ProcuratClassesTable
                key={selectedSchoolyearId}
                setImportWithParents={setImportWithParents}
                importWithParents={importWithParents}
                data={procuratClassesData}
                rowSelection={rowSelection}
                bpClassesSelectOpts={bpClassesSelectOpts}
                onRowSelectionChange={onRowSelectionChange}
                onImport={handleProfilesAssign}
                setBpClasses={handleSetBpClasses}
              />
            )}
          </BpCard>
        </>
      )}

      <Modal
        title={t('common.schoolyear')}
        isOpen={createSchoolYearModalOpen}
        onRequestClose={handleCreateSchoolYearClose}
      >
        <SchoolyearForm onClose={handleCreateSchoolYearClose} />
      </Modal>

      <ImportTutorsModal
        open={isTutorsModalOpen}
        classIds={classIds}
        onImport={handleImport}
        onClose={() => setIsTutorsModalOpen(false)}
      />
    </>
  );
};
